import { GrowthBook } from '@growthbook/growthbook'
import Vue from 'vue'

// Configuration GrowthBook
const growthbook = new GrowthBook({
  apiHost: import.meta.env.VITE_GROWTHBOOK_API_HOST || 'https://cdn.growthbook.io',
  clientKey: import.meta.env.VITE_GROWTHBOOK_CLIENT_KEY || '',
  enableDevMode: import.meta.env.DEV,
  trackingCallback: (experiment, result) => {
    // Track experiment views avec Google Analytics
    if (window.gtag) {
      window.gtag('event', 'experiment_viewed', {
        experiment_id: experiment.key,
        variant_id: result.value
      })
    }
  },
  // Définir les attributs par défaut
  attributes: {
    id: '',
    role: 'free',
    createdAt: null,
    country: 'FR',
    browser: navigator.userAgent,
  }
})

// Features flags par défaut (override par GrowthBook API)
const defaultFeatures = {
  // Limites Free Tier
  project_limit_free: 2,
  video_duration_limit: 5, // minutes
  speech_to_text_limit: 10, // minutes par mois
  ai_filter_limit: 5, // utilisations par mois
  export_limit_per_project: 3,
  
  // Pricing
  pricing_monthly: 12.99,
  pricing_annual: 99,
  
  // CTA Wording
  cta_wording: 'upgrade', // 'upgrade', 'unlock', 'try_pro'
  
  // UI/UX
  banner_position: 'top', // 'top', 'bottom', 'modal', 'none'
  show_watermark_free: true,
  slow_processing_free: false, // ralentir traitement en free
  
  // Triggers
  trigger_timing: 'immediate', // 'immediate', 'delayed', 'on_feature'
  trigger_frequency_hours: 3,
  
  // Social Proof
  show_social_proof: true,
  show_urgency_timer: false,
  
  // Onboarding
  show_pro_trial_offer: false,
  trial_days: 7,
}

// Charger les features depuis l'API GrowthBook
async function loadFeatures() {
  // Si pas de client key, utiliser juste les valeurs par défaut
  if (!import.meta.env.VITE_GROWTHBOOK_CLIENT_KEY) {
    console.log('[GrowthBook] No client key configured, using default features')
    return
  }
  
  try {
    await growthbook.loadFeatures({ timeout: 2000 })
    console.log('[GrowthBook] Features loaded:', growthbook.getFeatures())
  } catch (err) {
    console.warn('[GrowthBook] Error loading features, using defaults:', err.message)
    // Utiliser les valeurs par défaut en cas d'erreur
  }
}

// Plugin Vue
const GrowthBookPlugin = {
  install(Vue) {
    // Rendre GrowthBook accessible globalement
    Vue.prototype.$growthbook = growthbook
    
    // Helper pour obtenir une feature
    Vue.prototype.$feature = (key, fallback) => {
      return growthbook.getFeatureValue(key, fallback ?? defaultFeatures[key])
    }
    
    // Helper pour vérifier si une feature est active
    Vue.prototype.$isFeatureOn = (key) => {
      return growthbook.isOn(key)
    }
    
    // Helper pour exécuter un A/B test
    Vue.prototype.$runExperiment = (experimentKey) => {
      return growthbook.run({
        key: experimentKey,
        variations: [0, 1] // Par défaut 2 variations
      })
    }
  }
}

// Mettre à jour les attributs utilisateur
export function updateUserAttributes(user) {
  if (!user) return
  
  growthbook.setAttributes({
    id: user.sub || user.id,
    role: user.role || 'free',
    email: user.email,
    createdAt: user.created_at || user.createdAt,
    country: user.country || 'FR',
    browser: navigator.userAgent,
  })
  
  console.log('[GrowthBook] User attributes updated:', growthbook.getAttributes())
}

// Obtenir les limites basées sur le rôle et les A/B tests
export function getLimits(userRole = 'free') {
  // Sur iOS Capacitor, l'app est payante donc aucune limite
  const isNativePlatform = window.Capacitor && window.Capacitor.isNativePlatform()
  
  if (isNativePlatform) {
    console.log('[GrowthBook] Native platform detected - Unlimited access (paid app)')
    return {
      projectLimit: Infinity,
      videoDurationLimit: Infinity,
      speechToTextLimit: Infinity,
      aiFilterLimit: Infinity,
      exportLimitPerProject: Infinity,
      hasWatermark: false,
      slowProcessing: false,
      hasAudioRecordingAccess: true,
    }
  }
  
  const isPro = userRole === 'pro' || userRole === 'basic'
  
  if (isPro) {
    return {
      projectLimit: Infinity,
      videoDurationLimit: Infinity,
      speechToTextLimit: Infinity,
      aiFilterLimit: Infinity,
      exportLimitPerProject: Infinity,
      hasWatermark: false,
      slowProcessing: false,
      hasAudioRecordingAccess: true,
    }
  }
  
  // Pour les free users sur web, utiliser les valeurs des A/B tests
  return {
    projectLimit: growthbook.getFeatureValue('project_limit_free', defaultFeatures.project_limit_free),
    videoDurationLimit: growthbook.getFeatureValue('video_duration_limit', defaultFeatures.video_duration_limit),
    speechToTextLimit: growthbook.getFeatureValue('speech_to_text_limit', defaultFeatures.speech_to_text_limit),
    aiFilterLimit: growthbook.getFeatureValue('ai_filter_limit', defaultFeatures.ai_filter_limit),
    exportLimitPerProject: growthbook.getFeatureValue('export_limit_per_project', defaultFeatures.export_limit_per_project),
    hasWatermark: growthbook.getFeatureValue('show_watermark_free', defaultFeatures.show_watermark_free),
    slowProcessing: growthbook.getFeatureValue('slow_processing_free', defaultFeatures.slow_processing_free),
    hasAudioRecordingAccess: false, // Restriction: enregistrement audio réservé aux Pro
  }
}

// Obtenir le pricing actuel (pour A/B tests)
export function getPricing() {
  return {
    monthly: growthbook.getFeatureValue('pricing_monthly', defaultFeatures.pricing_monthly),
    annual: growthbook.getFeatureValue('pricing_annual', defaultFeatures.pricing_annual),
  }
}

// Obtenir le wording du CTA
export function getCTAWording() {
  const wording = growthbook.getFeatureValue('cta_wording', defaultFeatures.cta_wording)
  
  const wordings = {
    upgrade: {
      fr: 'Passer PRO',
      en: 'Upgrade to PRO'
    },
    unlock: {
      fr: 'Débloquer tout',
      en: 'Unlock Everything'
    },
    try_pro: {
      fr: 'Essayer PRO 7j',
      en: 'Try PRO 7 days'
    }
  }
  
  return wordings[wording] || wordings.upgrade
}

// Initialiser GrowthBook
loadFeatures()

export default GrowthBookPlugin
export { growthbook, defaultFeatures }

